import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const AuthPopup = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    identifier: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({
    fullName: false,
    identifier: false,
    password: false,
    confirmPassword: false,
    agreeTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, signup, error } = useAuth();
  const navigate = useNavigate();

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: '',
    color: 'red'
  });

  useEffect(() => {
    // Validate form on input changes (only touched fields)
    validateForm();
  }, [formData, isLogin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Mark field as touched when it changes
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const validateIdentifier = (identifier) => {
    if (!identifier) return 'Email or phone number is required';
    
    if (identifier.includes('@')) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
        return 'Please enter a valid email address';
      }
    } else {
      const digitsOnly = identifier.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        return 'Phone number must be at least 10 digits';
      }
      if (!/^[\d\s+-]{10,15}$/.test(identifier)) {
        return 'Please enter a valid phone number';
      }
    }
    return '';
  };

  const checkPasswordStrength = (password) => {
    if (!password) return { score: 0, message: '', color: 'red' };
    
    const hasMinLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    const strength = [hasMinLength, hasUpper, hasLower, hasNumber, hasSpecial]
      .filter(Boolean).length;
    
    let message = '';
    let color = 'red';
    
    if (strength <= 2) {
      message = 'Weak';
      color = 'red';
    } else if (strength === 3) {
      message = 'Moderate';
      color = 'orange';
    } else if (strength === 4) {
      message = 'Strong';
      color = 'blue';
    } else {
      message = 'Very Strong';
      color = 'green';
    }
    
    return { score: strength, message, color };
  };

  const validateForm = (forSubmission = false) => {
    const newErrors = {};
    
    // Validate identifier (only if touched or for submission)
    if (touched.identifier || forSubmission) {
      const identifierError = validateIdentifier(formData.identifier);
      if (identifierError) newErrors.identifier = identifierError;
    }
    
    // Validate password
    if (touched.password || forSubmission) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }
    
    // Validate confirm password
    if (!isLogin && (touched.confirmPassword || forSubmission)) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    // Validate full name
    if (!isLogin && (touched.fullName || forSubmission)) {
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }
    }
    
    // Validate terms
    if (!isLogin && (touched.agreeTerms || forSubmission)) {
      if (!formData.agreeTerms) {
        newErrors.agreeTerms = 'You must agree to the terms';
      }
    }
    
    setErrors(newErrors);
    setPasswordStrength(checkPasswordStrength(formData.password));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mark all fields as touched and validate
    setTouched({
      fullName: true,
      identifier: true,
      password: true,
      confirmPassword: true,
      agreeTerms: true
    });
    
    if (!validateForm(true)) {
      setIsSubmitting(false);
      return;
    }
    
    try {
      if (isLogin) {
        const success = await login(formData.identifier, formData.password);
        if (success) {
          onClose();
          navigate('/dashboard');
        }
      } else {
        const email = formData.identifier.includes('@') ? formData.identifier : null;
        const phoneNumber = !formData.identifier.includes('@') ? formData.identifier : null;
        const success = await signup(formData.fullName, email, phoneNumber, formData.password);
        if (success) {
          const loginSuccess = await login(formData.identifier, formData.password);
          if (loginSuccess) {
            onClose();
            navigate('/dashboard');
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <div className="modal-overlay">
      <div className="auth-popup">
        <button className="close-button" onClick={onClose}>
          ×
        </button>
        <h2>{isLogin ? 'Login' : 'Sign Up'}</h2>
        {error && <p className="error">{error}</p>}
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="input-group">
              <input
                type="text"
                name="fullName"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.fullName ? 'error-input' : ''}
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>
          )}
          
          <div className="input-group">
            <input
              type="text"
              name="identifier"
              placeholder="Email or Phone Number"
              value={formData.identifier}
              onChange={handleChange}
              onBlur={handleBlur}
              className={errors.identifier ? 'error-input' : ''}
            />
            {errors.identifier && <span className="error-message">{errors.identifier}</span>}
          </div>
          
          <div className="input-group">
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.password ? 'error-input' : ''}
              />
              <button 
                type="button" 
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
            {!isLogin && formData.password && (
              <div className="password-strength">
                <div 
                  className="strength-meter"
                  style={{ 
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    backgroundColor: passwordStrength.color
                  }}
                ></div>
                <span className="strength-text">
                  Strength: {passwordStrength.message}
                </span>
              </div>
            )}
          </div>
          
          {!isLogin && (
            <>
              <div className="input-group">
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.confirmPassword ? 'error-input' : ''}
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="error-message">{errors.confirmPassword}</span>
                )}
              </div>
              
              <div className="input-group">
                <label className="terms-checkbox">
                  <input
                    type="checkbox"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  I agree to the Terms and Conditions
                </label>
                {errors.agreeTerms && (
                  <span className="error-message">{errors.agreeTerms}</span>
                )}
              </div>
            </>
          )}
          
          <button 
            type="submit" 
            className="primary-button"
            disabled={isSubmitting || (Object.keys(errors).length > 0 && Object.values(touched).some(Boolean))}
          >
            {isSubmitting ? (
              <span className="spinner">Processing...</span>
            ) : (
              isLogin ? 'Login' : 'Sign Up'
            )}
          </button>
        </form>
        
        <button 
          className="toggle-button" 
          onClick={() => setIsLogin(!isLogin)}
          disabled={isSubmitting}
        >
          {isLogin ? 'Need an account? Sign Up' : 'Have an account? Login'}
        </button>
      </div>
      
      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .auth-popup {
          background: white;
          padding: 32px;
          border-radius: 12px;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          position: relative;
        }

        .close-button {
          position: absolute;
          top: 10px;
          right: 10px;
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #1a237e;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .close-button:hover {
          color: #0d1757;
        }

        h2 {
          margin-bottom: 20px;
          color: #1a237e;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .error {
          color: red;
          margin-bottom: 16px;
          font-size: 0.9rem;
        }

        .input-group {
          margin-bottom: 16px;
        }

        input {
          width: 100%;
          padding: 12px;
          border: 1px solid #e0e4ec;
          border-radius: 6px;
          font-size: 1rem;
        }

        .error-input {
          border-color: red;
        }

        .error-message {
          color: red;
          font-size: 0.8rem;
          margin-top: 4px;
          display: block;
        }

        .password-input-container {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          padding: 0;
        }

        .password-strength {
          margin-top: 8px;
        }

        .strength-meter {
          height: 4px;
          border-radius: 2px;
          transition: all 0.3s ease;
        }

        .strength-text {
          font-size: 0.8rem;
          color: #666;
          display: block;
          margin-top: 4px;
        }

        .terms-checkbox {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
          color: #1a237e;
        }

        .terms-checkbox input {
          width: auto;
          margin-right: 8px;
        }

        .primary-button {
          width: 100%;
          background: #1a237e;
          color: white;
          padding: 12px;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        }

        .primary-button:hover:not(:disabled) {
          background: #0d1757;
        }

        .primary-button:disabled {
          background: #cccccc;
          cursor: not-allowed;
        }

        .spinner {
          display: inline-block;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .toggle-button {
          width: 100%;
          background: none;
          border: none;
          color: #1a237e;
          font-weight: 500;
          cursor: pointer;
          margin-top: 10px;
          text-align: center;
        }

        .toggle-button:hover:not(:disabled) {
          text-decoration: underline;
        }

        .toggle-button:disabled {
          color: #cccccc;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default AuthPopup;